/* eslint-disable @typescript-eslint/no-magic-numbers */
/* eslint-disable import/prefer-default-export */
import { UseMutationOptions, UseQueryResult } from '@tanstack/react-query';
import { useCallback } from 'react';

import Constants from '@/Constants';
import { useApiMutation, useApiQuery } from '@/hooks/useApi';
import logger from '@/logger';
import {
  AdminFeedbackParameters,
  AdminFeedbackResponse,
  AdminLoginResponse,
  AdminSubjectListResponse,
  GenerateTestCodeRequest,
  GenerateTestCodeResponse,
  GenericApiResponse,
  GetDetailsResponse,
  GetOfflineZipUpdateDetailsResponse,
  GetOfflineZipUpdatesResponse,
  ValidateCenterResponse,
  makeRequest,
} from '@/services/api';
import useStore from '@/store';
import { ErrorCode } from '@/utils/errorMessages';
import {
  createKey,
  getAdminCommonHeaders,
  getSessionItem,
  isApiResponseSuccess,
} from '@/utils/helper';

export const useValidateCenter = (
  options?: Omit<
    UseMutationOptions<
      GenericApiResponse<ValidateCenterResponse>,
      unknown,
      { programCode: string; password: string; adminCode: string },
      unknown
    >,
    'mutationFn'
  >,
) => {
  const isApiErrorHandlingRequired = useCallback(
    (response: GenericApiResponse<ValidateCenterResponse>): boolean => {
      if (
        response?.data?.code === ErrorCode.InvalidAdminCode ||
        response?.data?.code === ErrorCode.InvalidProgramCode ||
        response?.data?.code === ErrorCode.IncorrectPassword ||
        response?.data?.code === ErrorCode.AdminAndProgramCodeMismatch
      ) {
        return false;
      }
      return true;
    },
    [],
  );
  return useApiMutation({
    mutationKey: createKey(Constants.VALIDATE_CENTER_URL),
    mutationFn: async ({ programCode, password, adminCode }) => {
      const response = await makeRequest<ValidateCenterResponse>(Constants.VALIDATE_CENTER_URL, {
        programCode,
        password,
        adminCode,
      });
      return response;
    },
    retry: false,
    apiErrorHandling: (response) => isApiErrorHandlingRequired(response),
    ...options,
  });
};

export const useAdminLogin = (
  options?: Omit<
    UseMutationOptions<
      GenericApiResponse<AdminLoginResponse>,
      unknown,
      { password: string; adminId: string },
      unknown
    >,
    'mutationFn'
  >,
) => {
  const { setCurrentAdminSession, setServerTime } = useStore();
  const isApiErrorHandlingRequired = useCallback(
    (response: GenericApiResponse<AdminLoginResponse>): boolean => {
      return response?.data?.code !== ErrorCode.IncorrectPassword;
    },
    [],
  );
  return useApiMutation({
    mutationKey: createKey(Constants.ADMIN_LOGIN_URL),
    mutationFn: async ({ adminId, password }: { password: string; adminId: string }) => {
      const response = await makeRequest<AdminLoginResponse>(Constants.ADMIN_LOGIN_URL, {
        adminId,
        password,
      });
      if (isApiResponseSuccess(response) && response.data.sessionId) {
        setCurrentAdminSession({ sessionId: response.data.sessionId, adminId });
        if (response.data.serverTime) {
          setServerTime(response.data.serverTime);
        }
      }
      return response;
    },
    apiErrorHandling: (response) => isApiErrorHandlingRequired(response),
    retry: false,
    ...options,
  });
};

export const useAdminLogout = (
  options?: Omit<
    UseMutationOptions<GenericApiResponse<string>, unknown, void, unknown>,
    'mutationFn'
  >,
) => {
  return useApiMutation({
    mutationKey: createKey(Constants.ADMIN_LOGOUT_URL),
    mutationFn: async () => {
      return makeRequest<string>(Constants.ADMIN_LOGOUT_URL, {}, getAdminCommonHeaders());
    },
    retry: false,
    ...options,
  });
};

export const useResetApp = (
  options?: Omit<
    UseMutationOptions<GenericApiResponse<string>, unknown, void, unknown>,
    'mutationFn'
  >,
) => {
  return useApiMutation({
    mutationKey: createKey(Constants.RESET_APP),
    mutationFn: async () => {
      const refreshToken = localStorage.getItem('refreshToken');
      return makeRequest<string>(Constants.RESET_APP, { refreshToken }, getAdminCommonHeaders());
    },
    retry: false,
    ...options,
  });
};

export const useSubmitAdminFeedback = (
  options?: Omit<
    UseMutationOptions<
      GenericApiResponse<AdminFeedbackResponse>,
      unknown,
      AdminFeedbackParameters,
      unknown
    >,
    'mutationFn'
  >,
) => {
  return useApiMutation({
    mutationKey: createKey(Constants.SUBMIT_ADMIN_FEEDBACK),
    mutationFn: async ({
      mobile,
      email,
      category,
      description,
      platform,
      deviceId,
      appVersion,
      deviceMetadata,
    }: AdminFeedbackParameters) => {
      const response = await makeRequest<AdminFeedbackResponse>(
        Constants.SUBMIT_ADMIN_FEEDBACK,
        {
          mobile,
          email,
          category,
          description,
          platform,
          deviceId,
          appVersion,
          deviceMetadata,
        },
        getAdminCommonHeaders(),
      );
      return response;
    },
    retry: false,
    ...options,
  });
};

export const useGetAdminDetails = () => {
  return useApiQuery({
    queryKey: createKey(Constants.GET_ADMIN_DETAILS),
    queryFn: () =>
      makeRequest<GetDetailsResponse>(Constants.GET_ADMIN_DETAILS, {}, getAdminCommonHeaders()),
    enabled: !!localStorage.getItem('adminId') && !!getSessionItem('adminSessionId'),
    select: (data) => data.data,
  });
};

export const useGetOfflineZipUpdates = (
  options?: Omit<
    UseMutationOptions<
      GenericApiResponse<GetOfflineZipUpdatesResponse>,
      unknown,
      { zipCode: string },
      unknown
    >,
    'mutationFn'
  >,
) => {
  return useApiMutation({
    mutationKey: createKey(Constants.GET_OFFLINE_ZIP_UPDATES_URL),
    mutationFn: async ({ zipCode }: { zipCode: string }) => {
      logger.info('[API] getOfflineZipUpdates called with zipCode:', zipCode);
      const response = await makeRequest<GetOfflineZipUpdatesResponse>(
        Constants.GET_OFFLINE_ZIP_UPDATES_URL,
        { zipCode },
        getAdminCommonHeaders(),
      );
      return response;
    },
    retry: false,
    ...options,
  });
};

export const useGetOfflineZipUpdateDetails = (
  options?: Omit<
    UseMutationOptions<
      GenericApiResponse<GetOfflineZipUpdateDetailsResponse>,
      unknown,
      { id: string },
      unknown
    >,
    'mutationFn'
  >,
) => {
  return useApiMutation({
    mutationKey: createKey(Constants.GET_OFFLINE_ZIP_UPDATE_DETAILS_URL),
    mutationFn: async ({ id }: { id: string }) => {
      logger.info('[API] getOfflineZipUpdateDetails called with id:', id);
      const response = await makeRequest<GetOfflineZipUpdateDetailsResponse>(
        Constants.GET_OFFLINE_ZIP_UPDATE_DETAILS_URL,
        { id },
        getAdminCommonHeaders(),
      );
      logger.info('[API] getOfflineZipUpdateDetails response status:', response?.status);
      return response;
    },
    retry: false,
    ...options,
  });
};

export const useAdminGetSubjectList = (): UseQueryResult<AdminSubjectListResponse, unknown> => {
  return useApiQuery({
    queryKey: createKey(Constants.ADMIN_GET_SUBJECT_LIST_URL),
    queryFn: () =>
      makeRequest<AdminSubjectListResponse>(
        Constants.ADMIN_GET_SUBJECT_LIST_URL,
        {},
        getAdminCommonHeaders(),
      ),
    enabled: !!localStorage.getItem('adminId') && !!getSessionItem('adminSessionId'),
    select: (data) => data.data,
  });
};

export const useAdminGenerateCode = () => {
  return useApiMutation({
    mutationKey: createKey(Constants.ADMIN_GENERATE_CODE_URL),
    mutationFn: async ({ subjectId, type }: GenerateTestCodeRequest) => {
      const response = await makeRequest<GenerateTestCodeResponse>(
        Constants.ADMIN_GENERATE_CODE_URL,
        { subjectId, type },
        getAdminCommonHeaders(),
      );
      return response;
    },
    retry: false,
  });
};
